import type { ColumnConfigSnapshot } from '@/stores/persistentTableStore';
import type { TableColumnStaticProps } from '@/types/tableTypes';

/**
 * Whether the table shows a column, given the stored map of hidden columns.
 *
 * A column with an entry follows its entry. A column with no entry shows, unless it is
 * hidden by default. So a browser that stored its columns before such a column shipped,
 * and a saved view that never mentions it, both keep it hidden.
 *
 * The table and the saved-view pruner both read this. If they disagreed, a saved view
 * would read as changed the moment it was opened.
 */
export function isColumnShown(
  column: Pick<TableColumnStaticProps, 'id' | 'hiddenByDefault'>,
  columnHidden: Record<string, boolean> | undefined,
): boolean {
  const hidden = columnHidden?.[column.id];
  return hidden === undefined ? !column.hiddenByDefault : !hidden;
}

/**
 * Deep-equal two column-config snapshots.
 *
 * `columnSort` is keyed by `field` and its **insertion order encodes multi-sort
 * priority** (see ColumnSettingsInspectorContent's saveHandler and
 * buildSnapshotFromLocalColumns), so the comparison walks its keys in
 * sequence rather than treating it as a bag. Two layouts with the same set of
 * sort fields but a different sort priority must compare as **unequal** — that's
 * how the "Update {name}" / dirty-dot UX detects a meaningful change.
 *
 * The other four maps (`columnOrder`, `columnHidden`, `columnPin`,
 * `columnSize`) are keyed by stable column id, so order is meaningless there
 * — a key-set + per-key value check is sufficient.
 */
export function columnConfigSnapshotsEqual(
  a: ColumnConfigSnapshot,
  b: ColumnConfigSnapshot,
): boolean {
  const aSortKeys = Object.keys(a.columnSort);
  const bSortKeys = Object.keys(b.columnSort);
  if (aSortKeys.length !== bSortKeys.length) return false;
  for (let i = 0; i < aSortKeys.length; i++) {
    if (aSortKeys[i] !== bSortKeys[i]) return false;
    if (a.columnSort[aSortKeys[i]] !== b.columnSort[bSortKeys[i]]) return false;
  }
  const flatMaps = ['columnOrder', 'columnHidden', 'columnPin', 'columnSize'] as const;
  for (const mapKey of flatMaps) {
    const aMap = a[mapKey] as Record<string, number | boolean>;
    const bMap = b[mapKey] as Record<string, number | boolean>;
    const aKeys = Object.keys(aMap);
    const bKeys = Object.keys(bMap);
    if (aKeys.length !== bKeys.length) return false;
    for (const k of aKeys) {
      if (!(k in bMap) || aMap[k] !== bMap[k]) return false;
    }
  }
  return true;
}

/**
 * A column's display ordinal. The pinned flag rides in the SIGN: pinned columns get
 * a negative ordinal so `compareColumnDisplayOrder` puts them first.
 *
 * The column-settings inspector writes ordinals, the saved-view pruner reconstructs
 * them, and both must agree. Rebuilding one without the sign makes pinned columns
 * quietly stop sorting first, which reads as a layout bug a long way from its cause.
 */
export function columnDisplayOrdinal(index: number, isPinned: boolean): number {
  return (index + 1) * (isPinned ? -1 : 1);
}

/**
 * The order the table actually renders a column in: ascending ordinal, with two
 * pinned columns compared by absolute value so their relative order holds.
 *
 * The render path and the saved-view pruner both sort with this. They held two
 * hand-synced copies of it, and if those ever disagreed a pruned view would reorder
 * columns on the way back in and read dirty the moment it loaded.
 */
export function compareColumnDisplayOrder(leftOrder: number, rightOrder: number): number {
  if (leftOrder < 0 && rightOrder < 0) {
    return Math.abs(leftOrder) - Math.abs(rightOrder);
  }
  return leftOrder - rightOrder;
}
